import { cloneState, type SyncrelState } from "./types";

export type ShareAccess = "edit" | "view";

export interface WorkspaceShareKeys {
  editKey: string;
  viewKey: string;
}

const TOKEN_ALPHABET = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

export const createShareToken = (length = 24): string => {
  const bytes = new Uint8Array(length);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (byte) => TOKEN_ALPHABET[byte % TOKEN_ALPHABET.length]).join("");
};

export const createWorkspaceRoomId = (token = createShareToken(22)): string => `syncrel-${token}`;

export const createWorkspaceShareKeys = (): WorkspaceShareKeys => ({
  editKey: `edit_${createShareToken()}`,
  viewKey: `view_${createShareToken()}`,
});

export const workspaceShareKeysFromUrl = (
  href = window.location.href,
  fallback = createWorkspaceShareKeys(),
): WorkspaceShareKeys => {
  const url = new URL(href);
  const access = url.searchParams.get("access");
  const share = url.searchParams.get("share");
  return {
    editKey: access === "edit" && share ? share : fallback.editKey,
    viewKey: access === "view" && share ? share : fallback.viewKey,
  };
};

export const ensureWorkspaceShareKeys = (
  state: SyncrelState,
  keys = createWorkspaceShareKeys(),
): SyncrelState => {
  if (state.workspace.share?.editKey && state.workspace.share.viewKey) return state;
  const next = cloneState(state);
  next.workspace.share = {
    editKey: state.workspace.share?.editKey || keys.editKey,
    viewKey: state.workspace.share?.viewKey || keys.viewKey,
  };
  return next;
};

export const workspaceShareLink = (
  room: string,
  access: ShareAccess,
  href = window.location.href,
  keys?: WorkspaceShareKeys,
): string => {
  const url = new URL(href);
  url.searchParams.set("room", room);
  url.searchParams.set("access", access);
  const shareKey = access === "view" ? keys?.viewKey : keys?.editKey;
  if (shareKey) url.searchParams.set("share", shareKey);
  return url.toString();
};

export const accessModeFromUrl = (
  href = window.location.href,
  state?: SyncrelState,
): ShareAccess => {
  const url = new URL(href);
  const requested = url.searchParams.get("access");
  if (requested === "view") return "view";
  if (requested !== "edit") return "edit";
  const editKey = state?.workspace.share?.editKey;
  if (!editKey) return "view";
  return url.searchParams.get("share") === editKey ? "edit" : "view";
};

export const shouldStartReadOnlyFromUrl = (href = window.location.href): boolean => (
  new URL(href).searchParams.has("access")
);
